import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "urbx_cart_v5";
const ECOCUP_TOUCHED_KEY = "urbx_cart_eco_touched_v1";
const ECOCUP_ID = "ecocup";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [ecocupProduct, setEcocupProduct] = useState(null);
  const [ecocupTouched, setEcocupTouched] = useState(() => {
    try {
      return localStorage.getItem(ECOCUP_TOUCHED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(ECOCUP_TOUCHED_KEY, ecocupTouched ? "1" : "0");
  }, [ecocupTouched]);

  const buildEcocupLine = useCallback(
    (qty = 1) => {
      if (!ecocupProduct) return null;
      return {
        instance_id: ECOCUP_ID,
        product_id: ecocupProduct.id,
        name: ecocupProduct.name,
        price_eur: ecocupProduct.price_eur,
        unit_label: ecocupProduct.unit_label,
        quantity: qty,
        stock_remaining: ecocupProduct.stock_remaining,
        is_alcohol: false,
        is_deposit: true,
        is_pack: false,
        image_url: ecocupProduct.image_url,
      };
    },
    [ecocupProduct]
  );

  // Max ecocups = (drinks qty, excluding food/deposit/pack) + (pack_n_ecocups × qty for packs)
  const computeMaxEcocupQty = (list) => {
    const drinkCount = list.reduce((s, it) => {
      if (it.is_deposit || it.is_pack) return s;
      if (it.category === "food") return s;
      return s + it.quantity;
    }, 0);
    const packEco = list.reduce((s, it) => {
      if (!it.is_pack) return s;
      return s + (it.pack_n_ecocups ?? 1) * it.quantity;
    }, 0);
    return drinkCount + packEco;
  };

  const ensureEcocup = useCallback(
    (list, touchedOverride) => {
      const touched = touchedOverride ?? ecocupTouched;
      const max = computeMaxEcocupQty(list);
      const ecoIdx = list.findIndex((it) => it.is_deposit);

      if (max === 0) {
        if (ecocupTouched) setEcocupTouched(false);
        return ecoIdx === -1 ? list : list.filter((it) => !it.is_deposit);
      }

      const targetQty = touched && ecoIdx !== -1 ? Math.min(list[ecoIdx].quantity, max) : max;

      if (ecoIdx === -1) {
        const line = buildEcocupLine(targetQty);
        return line ? [...list, line] : list;
      }
      if (list[ecoIdx].quantity === targetQty) return list;
      const next = [...list];
      next[ecoIdx] = { ...next[ecoIdx], quantity: targetQty };
      return next;
    },
    [buildEcocupLine, ecocupTouched]
  );

  useEffect(() => {
    if (!ecocupProduct) return;
    queueMicrotask(() => setItems((prev) => ensureEcocup(prev)));
  }, [ecocupProduct, ensureEcocup]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.instance_id === product.id);
      const stock = product.stock_remaining;
      let next;
      if (idx === -1) {
        next = [
          ...prev,
          {
            instance_id: product.id,
            product_id: product.id,
            name: product.name,
            price_eur: product.price_eur,
            unit_label: product.unit_label,
            quantity: Math.min(qty, stock),
            stock_remaining: stock,
            is_alcohol: product.is_alcohol,
            is_deposit: !!product.is_deposit,
            is_pack: false,
            image_url: product.image_url,
          },
        ];
      } else {
        next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(next[idx].quantity + qty, stock) };
      }
      return ensureEcocup(next);
    });
  };

  // Add a pack instance. components = [{product_id, name, quantity, is_alcohol, category}]
  const addPack = (pack, components) => {
    setItems((prev) => {
      const instance_id = `${pack.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const next = [
        ...prev,
        {
          instance_id,
          product_id: pack.id,
          name: pack.name,
          subtitle: pack.subtitle,
          price_eur: pack.price_eur,
          original_eur: pack.original_eur,
          unit_label: pack.unit_label || "pack",
          quantity: 1,
          stock_remaining: 9999,
          is_alcohol: components.some((c) => c.is_alcohol),
          is_deposit: false,
          is_pack: true,
          pack_n_ecocups: pack.pack_n_ecocups ?? 1,
          components,
          image_url: pack.image_url,
        },
      ];
      return ensureEcocup(next);
    });
  };

  const setQty = (instanceId, qty) => {
    const isEcocup = instanceId === ECOCUP_ID;
    if (isEcocup) setEcocupTouched(true);
    setItems((prev) => {
      const updated = prev
        .map((it) => {
          if (it.instance_id !== instanceId) return it;
          if (it.is_pack) return it; // packs are non-editable qty
          return { ...it, quantity: Math.max(0, Math.min(qty, it.stock_remaining)) };
        })
        .filter((it) => it.quantity > 0);
      return ensureEcocup(updated, isEcocup ? true : undefined);
    });
  };

  const remove = (instanceId) => {
    const isEcocup = instanceId === ECOCUP_ID;
    if (isEcocup) setEcocupTouched(true);
    setItems((prev) => {
      return ensureEcocup(
        prev.filter((it) => it.instance_id !== instanceId),
        isEcocup ? true : undefined
      );
    });
  };

  const clear = () => {
    setEcocupTouched(false);
    setItems([]);
  };

  const totals = useMemo(() => {
    const drinks = items.filter((it) => !it.is_deposit && !it.is_pack);
    const drinksCount = drinks.reduce((s, it) => s + it.quantity, 0);
    const packCount = items.filter((it) => it.is_pack).reduce((s, it) => s + it.quantity, 0);
    const cupCount = items.filter((it) => it.is_deposit).reduce((s, it) => s + it.quantity, 0);
    const total = items.reduce((s, it) => s + it.quantity * it.price_eur, 0);
    return {
      count: drinksCount + cupCount + packCount,
      drinksCount,
      packCount,
      cupCount,
      total: Math.round(total * 100) / 100,
    };
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        add,
        addPack,
        setQty,
        remove,
        clear,
        open,
        setOpen,
        setEcocupProduct,
        ecocupProduct,
        ECOCUP_ID,
        ...totals,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
